import * as React from 'react';
import { connect } from 'react-redux';

import { Link } from 'libs/router';

import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import NotificationBadge from 'components/notification-badge/NotificationBadge';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import CcmCampaignEditModal from 'modules/campaigns/components/ccm/components/modals/edit/CcmCampaignEditModal';

import { __t, applyTranslations } from 'utils/language';
import { isCcmModerator } from 'utils/account';
import { isInfluencerSignInDateReached } from 'modules/campaigns/components/ccm/utils/ccm-timeline';

import './ccm-campaigns-table-actions-cell.scss';

applyTranslations(require('./ccm-campaigns-table-actions-cell.i18n.json'));

import type {
  CampaignWithSubcampaignMeta,
  ContextMenuItem,
  Role
} from 'types';

type Action = {
  iconName: string,
  url: string,
  tooltip: string
};

type OwnProps = {
  campaign: CampaignWithSubcampaignMeta
};

type StateProps = {
  currentOrganizationId: number,
  roles: Array<Role>,
  unreadMessagesCount: number
};

type Props = OwnProps & StateProps;

const MSG_KEY = 'ccm_campaigns_table_actions_cell';
const MESSAGES_ICON_NAME = 'speech-bubble-custom-bg';
const PAYMENTS_ICON_NAME = 'euro-inside-circle-custom-bg';

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

const CcmCampaignsTableActionsCell: React.FC<Props> = ({
  campaign,
  currentOrganizationId,
  roles,
  unreadMessagesCount
}): JSX.Element => {
  const getContextMenuItems = (): Array<ContextMenuItem> => ([
    {
      type: 'modal',
      name: 'edit-campaign',
      text: __t(`${MSG_KEY}.context_menu.edit`),
      meta: {
        campaignId: campaign.id,
        organizationId: currentOrganizationId,
        brandProfileId: campaign?.brandProfile?.id
      },
      extra: {
        component: CcmCampaignEditModal
      },
      disabled: isInfluencerSignInDateReached(campaign?.timeline)
    }
  ]);

  const getBrandActions = (): Array<Action> => ([
    {
      iconName: 'offers-custom-bg',
      url: `/campaigns/ccm/${campaign.id}/overview`,
      tooltip: __t(`${MSG_KEY}.tooltips.overview`)
    },
    {
      iconName: 'hanger-custom-fg',
      url: `/campaigns/ccm/${campaign.id}/outfits`,
      tooltip: __t(`${MSG_KEY}.tooltips.outfits`)
    },
    {
      iconName: 'bar-chart-custom-bg',
      url: `/campaigns/ccm/${campaign.id}/report`,
      tooltip: __t(`${MSG_KEY}.tooltips.report`)
    }
  ]);

  const getModeratorActions = (): Array<Action> => [
    {
      iconName: 'offers-custom-bg',
      url: `/campaigns/ccm/${campaign.id}/participants/accepted`,
      tooltip: __t(`${MSG_KEY}.tooltips.offers`)
    },
    {
      iconName: MESSAGES_ICON_NAME,
      url: `/campaigns/ccm/${campaign.id}/messages`,
      tooltip: __t(`${MSG_KEY}.tooltips.messages`)
    },
    {
      iconName: 'bar-chart-custom-bg',
      url: `/campaigns/ccm/${campaign.id}/report`,
      tooltip: __t(`${MSG_KEY}.tooltips.report`)
    },
    {
      iconName: PAYMENTS_ICON_NAME,
      url: `/campaigns/ccm/${campaign.id}/payments`,
      tooltip: __t(`${MSG_KEY}.tooltips.payments`)
    }
  ];

  const actions = isCcmModerator(roles)
    ? getModeratorActions()
    : getBrandActions();

  return (
    <div className="ccm-campaigns-table-actions-cell">
      {
        actions.map(action => (
          <div
            key={`ccm-campaigns-table-actions-cell__action--${action.iconName}`}
            className="ccm-campaigns-table-actions-cell__action"
          >
            <Tooltip
              message={action.tooltip}
              width="auto-compact"
            >
              <Link to={action.url}>
                <SVGDynamicIcon
                  name={action.iconName}
                  customColor="grey"
                  clickable
                  width={22}
                  height={22}
                />
              </Link>
            </Tooltip>
            {
              action.iconName === MESSAGES_ICON_NAME && unreadMessagesCount > 0 && (
                <div className="ccm-campaigns-table-actions-cell__badge">
                  <NotificationBadge
                    value={unreadMessagesCount}
                    size="small"
                  />
                </div>
              )
            }
            {
              action.iconName === PAYMENTS_ICON_NAME && Boolean(campaign.budgetNote) && (
                <div className="ccm-campaigns-table-actions-cell__notification" />
              )
            }
          </div>
        ))
      }
      {
        isCcmModerator(roles) && (
          <div className="ccm-campaigns-table-actions-cell__action">
            <div className="ccm-campaigns-table-actions-cell__context-menu">
              <MoreButtonWithMenu
                width={20}
                contextMenuProps={{ items: getContextMenuItems() }}
              />
            </div>
          </div>
        )
      }
    </div>
  );
};

const mapStateToProps = ({
  account,
  organizations,
  unreadMessages
}, { campaign }): StateProps => {
  const unreadMessagesCount = (unreadMessages.conversations || [])
    .find((entry) => entry.campaignId === campaign.id)
    ?.unreadConversationsCount ?? 0;

  return {
    unreadMessagesCount,
    roles: account.roles,
    currentOrganizationId: organizations.currentOrganizationId
  };
};

export default connect(mapStateToProps)(CcmCampaignsTableActionsCell);
